import { isApiError } from '@/api/http/errors';
import { ShellHeader } from '@/app/shell-header';
import { useOperatorSession } from '@/auth/runtime';
import { isUuid } from '@/lib/validation';
import { StayAccessSection } from '@/pages/access/stay-access-section';
import { displayStayStatus, StayPeriod } from '@/pages/stays/stay-presentation';
import { Button, DetailPage, PageHeader } from '@mgrvtechgroup/admin-ds';
import { useQuery } from '@tanstack/react-query';
import type { ReactNode } from 'react';
import { Link, useParams, useSearchParams } from 'react-router';
import { hasStayReadAccess, stayDetailQuery, stayRuntime, type StayRuntime } from './stay-queries';
import { stayDetailState } from './stay-url';

export function StayDetailPage({ runtime = stayRuntime }: { runtime?: StayRuntime }) {
  const { stayId = '' } = useParams();
  const [params] = useSearchParams();
  const state = stayDetailState(params);
  const session = useOperatorSession(runtime.session);
  const allowed = hasStayReadAccess(runtime.session, session.generation);
  const valid = isUuid(stayId) && stayId === stayId.toLowerCase() && state.valid;
  const stay = useQuery(stayDetailQuery(runtime, session.generation, stayId, valid && allowed));
  const backHref = state.returnTo;
  const frame = (content: ReactNode) => (
    <DetailPage className="min-h-0 flex-1 overflow-y-auto">
      <ShellHeader>
        <PageHeader
          title="투숙 상세"
          description="현재 저장된 투숙 정보와 출입 기록을 확인합니다."
          className="items-start sm:items-center"
        />
      </ShellHeader>
      <DetailPage.Body className="min-h-0">
        <Button
          nativeButton={false}
          role="link"
          variant="outline"
          size="sm"
          className="w-fit self-start"
          render={<Link to={backHref} />}
        >
          투숙 목록으로
        </Button>
        {content}
      </DetailPage.Body>
    </DetailPage>
  );
  if (session.status !== 'authorized') return null;
  if (!valid)
    return frame(<p role="alert">투숙 주소가 올바르지 않습니다. 목록에서 다시 선택해 주세요.</p>);
  if (!allowed)
    return frame(
      <p role="alert">현재 예약 조회 권한이 없습니다. 투숙 상세를 표시하지 않습니다.</p>,
    );
  if (stay.isPending) return frame(<p role="status">투숙 저장 상태를 조회하고 있습니다.</p>);
  if (stay.isError)
    return frame(
      <div role="alert" className="space-y-2">
        <p>
          {isApiError(stay.error) && stay.error.status === 404
            ? '투숙을 찾을 수 없습니다.'
            : isApiError(stay.error) && stay.error.status === 403
              ? '현재 예약 조회 권한이 없습니다.'
              : isApiError(stay.error) && stay.error.kind === 'invalid-response'
                ? '투숙 정보를 불러오지 못했습니다. 관리자에게 문의해 주세요.'
                : '투숙 상세를 불러오지 못했습니다.'}
        </p>
        <Button variant="outline" onClick={() => void stay.refetch()}>
          다시 조회
        </Button>
      </div>,
    );
  if (stay.data.stay_id !== stayId)
    return frame(<p role="status">현재 투숙을 확인하고 있습니다.</p>);
  return frame(
    <>
      <DetailPage.Section title="현재 저장 요약">
        <dl className="grid min-w-0 gap-3 sm:grid-cols-2 xl:grid-cols-3">
          <div>
            <dt>현재 저장 상태</dt>
            <dd>{displayStayStatus(stay.data.status)}</dd>
          </div>
          <div className="sm:col-span-3">
            <dt>저장 투숙 기간</dt>
            <dd>
              <StayPeriod startAt={stay.data.start_at} endAt={stay.data.end_at} />
            </dd>
          </div>
        </dl>
        <details className="body-sm mt-3">
          <summary className="cursor-pointer">투숙 식별 정보 · {stayId.slice(0, 8)}</summary>
          <dl>
            <dt>투숙 번호</dt>
            <dd className="break-all select-text">{stay.data.stay_id}</dd>
          </dl>
        </details>
        <p className="body-sm text-muted-foreground mt-3">
          현재 저장된 상태와 기간은 저장 사실이며, 실제 투숙·점유나 출입 준비 상태를 확정하지
          않습니다.
        </p>
      </DetailPage.Section>
      <DetailPage.Section title="연결 예약의 저장 정보">
        {stay.data.sources.length ? (
          <ul className="space-y-2">
            {stay.data.sources.map((source) => (
              <li key={source.source_id} className="space-y-1">
                <details>
                  <summary className="cursor-pointer">
                    예약 {source.source_id.slice(0, 8)} · 식별 정보
                  </summary>
                  <p className="break-all select-text">예약 ID {source.source_id}</p>
                </details>
                <p>저장 예약 상태 {source.status}</p>
                <StayPeriod startAt={source.start_at} endAt={source.end_at} />
              </li>
            ))}
          </ul>
        ) : (
          <p>연결 예약의 저장 정보가 없습니다.</p>
        )}
      </DetailPage.Section>
      <StayAccessSection
        stayId={stayId}
        showStaySummary={false}
        runtime={{
          client: runtime.client,
          session: runtime.session,
          apiNamespace: runtime.apiNamespace,
        }}
      />
    </>,
  );
}
